import React, { useState } from 'react';
import { connect } from 'react-redux';
import NavMenu from './NavMenu';
import ActivationBanner from './ActivationBanner';
import ReactivationNotice from './ReactivationNotice';
import QrScan from './QrScan';

// The single shared top bar for every authenticated page (Dashboard,
// Edit Profile/Settings, Leads, Analytics) -- previously this only
// existed inline in Dashboard.js's own .tuto1 div, so every other
// logged-in page either had a stripped-down copy (EditProfile.js, just
// NavMenu + its own Back control) or nothing at all (Leads.js,
// Analytics.js). Lifting it here means the burgundy/black activatedAt
// switch, the hamburger, and the QR modal exist in exactly one place
// instead of drifting across separate copies. Owns the QR modal's
// show/hide state itself (previously Dashboard-local) since the trigger
// (the QR icon below) and the modal are both part of this one component
// now.
//
// `back` is an optional ReactNode for pages that need a Back control
// (EditProfile, Leads, Analytics) -- deliberately a slot, not a
// backTo/onBack prop, since what "back" means differs per page
// (EditProfile's own Settings view is a local-state toggle, not
// navigation) and each page already has that logic; this component
// only owns where it renders, not what it does. Omitted entirely on
// Dashboard, which has nothing to go back to.
const AppTopBar = ({ user, back }) => {
  const [show, setshow] = useState('');

  return (
    <>
      <div
        className="tuto1"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 10px',
          color: 'white',
          // .tuto1's own background (style.css) is solid black --
          // overridden inline here only, so Landing.js/Policy.js/
          // Terms.js (the class's other users) are untouched.
          backgroundColor: user && user.activatedAt ? undefined : '#7a2e21',
        }}
      >
        {/* NavMenu and the QR icon are both fixed-size, so flexShrink:
            0 keeps them from ever being squeezed by the middle
            region -- that region (flex: 1, minWidth: 0) is the only
            one allowed to shrink, and ActivationBanner.js's own
            narrow-viewport CSS drops its text before the button
            alone could ever overflow it. */}
        <div style={{ flexShrink: 0 }}>
          <NavMenu />
        </div>
        {/* Grouped with the hamburger (both "leading" controls) rather
            than replacing it or sitting to its left -- either of those
            would either break the hamburger's always-top-left
            convention or cost a page its NavMenu access (Settings/
            Leads/Analytics/Log out) whenever Back is showing. */}
        {back && <div style={{ flexShrink: 0, marginLeft: '10px' }}>{back}</div>}
        <div
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <ActivationBanner />
        </div>
        <div
          style={{
            flexShrink: 0,
            backgroundColor: 'white',
            padding: '1px',
            cursor: 'pointer',
          }}
          onClick={() => setshow('show')}
        >
          <img
            src="https://firebasestorage.googleapis.com/v0/b/digitalcontacts-2699b.appspot.com/o/images%2Fqr-code.svg?alt=media&token=ed5b1895-ce13-42ff-abb6-605d5313f6bf"
            width="25"
            className="showPopup"
            target="#profileQrCon"
            alt="QR code"
          />
        </div>
      </div>

      {/* user can briefly be null while GET /api/auth/current is still
          in flight on a page that doesn't itself gate on `loading`
          (this component has no such guarantee from its callers) --
          QrScan reads user.avatarUrl/name/lengthId directly, so it's
          skipped until there's a real user to show. */}
      {user && <QrScan user={user} show={show} setshow={setshow} />}

      {/* Renders nothing unless the server says there is a notice
          outstanding, so this costs a null check on every signed-in
          page and nothing else. Mounted here rather than in Dashboard
          so it also catches /add_profile_image, which is where a
          non-admin login actually lands first. */}
      <ReactivationNotice />
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.registerUser.user,
});

export default connect(mapStateToProps)(AppTopBar);
